
import Vue from "vue";
import ViewJob from "@/components/recruiter/jobs/job_listing/ViewJob.vue";
import GuidedTooltip from "@/components/shared/GuidedTooltip.vue";
import { JobsWithCandidates } from "@/store/modules/recruiter/interfaces";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { FETCH_MATCHING_CANDIDATES, FETCH_MATCHING_CANDIDATES_V2 } from "@/store/modules/recruiter/constants";
import { parseStrToInt } from "@/utils/global";
import { ROOT_ERROR } from "@/store/modules/root/constants";
import { GET_JOB_BY_ID } from "@/store/modules/common/constants";
import { 
  TOOLTIP_VISIBLE,
  TOOLTIP_CURRENT_ID,
  TOOLTIP_CURRENT_STEP
} from "@/store/modules/tooltip/constants";
import ViewJobLoading from "@/components/recruiter/jobs/ViewJobLoading.vue";

export default Vue.extend({
  name: "ViewJobWithCandidates",
  components: { ViewJobLoading, ViewJob, GuidedTooltip },
  beforeRouteEnter(to, from, next) {
    const param_id = to.params.id;
    if (!param_id || !parseStrToInt(param_id)) next(false);
    else next();
  },
  beforeRouteUpdate(to, from, next) {
    const param_id = to.params.id;
    if (!param_id || !parseStrToInt(param_id)) next(false);
    else next();
  },
  data() {
    return {
      job: null as null | JobsWithCandidates,
      loading: true
    };
  },
  computed: {
    ...mapGetters("recruiter", {
      get_job_with_candidates: FETCH_MATCHING_CANDIDATES
    }),
    ...mapGetters("tooltip", {
      tooltipsVisible: TOOLTIP_VISIBLE,
      currentTooltipId: TOOLTIP_CURRENT_ID,
      currentStep: TOOLTIP_CURRENT_STEP,
      currentTooltipConfig: "currentTooltipConfig"
    })
  },
  async mounted() {
    this.loading = true;
    const jid = parseStrToInt(this.$route.params.id);
    if (!jid) {
      this.root_error("Invalid Job");
      await this.$router.push("/recruiter/jobs");
      return;
    }
    // If the job exists in store => get
    const response = this.get_job_with_candidates(jid);
    if (response) {
      this.job = response;
      // Still fetch matching candidates even if job is in store
      await this.fetch_matching_candidates_for_job(jid);
      this.loading = false;
      // Initialize tooltips for job details page
      this.initializeJobDetailsTooltips();
      return;
    }
    // If the job not exists in store => fetch from backend
    await this.process_job_from_backend(jid);
    // Initialize tooltips for job details page
    this.initializeJobDetailsTooltips();
  },
  methods: {
    ...mapMutations({
      root_error: ROOT_ERROR
    }),
    ...mapActions("common", {
      fetch_job_by_id: GET_JOB_BY_ID
    }),
    ...mapActions("recruiter", {
      fetch_matching_candidates: FETCH_MATCHING_CANDIDATES,
      fetch_matching_candidates_v2: FETCH_MATCHING_CANDIDATES_V2
    }),
    ...mapActions("tooltip", {
      setTooltipVisibility: "setTooltipVisibility",
      nextTooltipStep: "nextTooltipStep",
      previousTooltipStep: "previousTooltipStep",
      completeTooltip: "completeTooltip",
      skipTooltip: "skipTooltip"
    }),
    // Initialize tooltips for job details page
    initializeJobDetailsTooltips() {
      // Check if user is a recruiter and trigger job details tooltip
      setTimeout(() => {
        this.$store.dispatch('tooltip/checkRouteTooltips', { 
          userRole: 'recruiter', 
          routePath: this.$route.path 
        });
      }, 1000); // Delay to ensure page is rendered
    },
    // Tooltip event handlers
    onStepChanged(step: number) {
      // Step change is handled by the component internally
    },
    onTooltipSkipped() {
      this.skipTooltip();
    },
    onTooltipFinished() {
      this.completeTooltip();
    },
    onTooltipClosed() {
      this.skipTooltip();
    },
    async fetch_matching_candidates_for_job(jid: number) {
      // Fetching matching candidates using v2 API
      try {
        const matching_candidates = await this.fetch_matching_candidates_v2({
          job_ids: [jid]
        });
        if (matching_candidates && this.job) {
          this.job.matching_candidates = matching_candidates.results;
        }
      } catch (error) {
        console.error("Failed to fetch matching candidates:", error);
      }
    },
    async process_job_from_backend(jid: number) {
      // Fetching job
      const response = await this.fetch_job_by_id(jid);
      if (!response) {
        this.root_error("Invalid Job");
        await this.$router.push("/recruiter/jobs");
        this.loading = false;
        return;
      }
      this.job = response;
      // Fetch matching candidates
      await this.fetch_matching_candidates_for_job(jid);
      this.loading = false;
    }
  }
});
